import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "activePercentages", "sharedIngredients"]

  connect() {
    this.setIngredientStepBadge()
    this.setIngredientActivePercentages()
    this.highlightFungalAcneTriggers()
    this.highlightPoreCloggingIngredients()
    this.highlightSebDermTriggers()

    // Load user preferences into memory
    this.loadUserPreferences()

    // Auto-highlight user's loved and avoided ingredients on page load
    // This is used in the normal product view to always show preferences
    // In comparison view, preferences are controlled by toggle buttons instead
    if (this.element.dataset.autoHighlightPreferences === "true") {
      this.styleIngredientsOnPage()
    }

    // Only highlight common ingredients by default (checkbox is checked)
    this.highlightSharedIngredients()

    // Listen for Turbo events in case content is loaded dynamically
    document.addEventListener("turbo:frame-load", () => {
      this.highlightSharedIngredients()
    })

    document.addEventListener("turbo:after-stream-render", () => {
      this.highlightSharedIngredients()
    })
  }

  async loadUserPreferences() {
    this.userPreferences = await this.fetchUserIngredientPreferences()
  }

  toggleCommon(event) {
    if (event.target.checked) {
      this.highlightSharedIngredients()
    } else {
      this.unhighlightSharedIngredients()
    }
  }

  toggleLoves(event) {
    if (event.target.checked) {
      this.highlightLoves()
    } else {
      this.unhighlightLoves()
    }
  }

  toggleAvoids(event) {
    if (event.target.checked) {
      this.highlightAvoids()
    } else {
      this.unhighlightAvoids()
    }
  }

  toggleWatched(event) {
    if (event.target.checked) {
      this.highlightWatched()
    } else {
      this.unhighlightWatched()
    }
  }

  async highlightWatched() {
    if (!this.userPreferences) {
      await this.loadUserPreferences()
    }
    const { watched } = this.userPreferences

    this.element.querySelectorAll("[data-ingredient-id]").forEach((ingredient) => {
      const ingredientId = parseInt(ingredient.dataset.ingredientId, 10)
      if (watched.has(ingredientId)) {
        ingredient.classList.add("bg-orange-100", "text-orange-950", "font-medium", "rounded-2xl", "my-2", "px-2", 'py-0.5')
      }
    })
  }

  unhighlightWatched() {
    this.element.querySelectorAll("[data-ingredient-id]").forEach((ingredient) => {
      // Only remove color classes
      ingredient.classList.remove("bg-orange-100", "text-orange-950")
      // Check if ingredient should still be highlighted by other categories
      this.cleanupBaseClasses(ingredient)
    })
  }

  async highlightLoves() {
    if (!this.userPreferences) {
      await this.loadUserPreferences()
    }
    const { liked } = this.userPreferences

    this.element.querySelectorAll("[data-ingredient-id]").forEach((ingredient) => {
      const ingredientId = parseInt(ingredient.dataset.ingredientId, 10)
      if (liked.has(ingredientId)) {
        ingredient.classList.add("bg-green-100", "text-green-950", "font-medium", "rounded-2xl", "my-2", "px-2", 'py-0.5')
      }
    })
  }

  unhighlightLoves() {
    this.element.querySelectorAll("[data-ingredient-id]").forEach((ingredient) => {
      // Only remove color classes
      ingredient.classList.remove("bg-green-100", "text-green-950")
      // Check if ingredient should still be highlighted by other categories
      this.cleanupBaseClasses(ingredient)
    })
  }

  async highlightAvoids() {
    if (!this.userPreferences) {
      await this.loadUserPreferences()
    }
    const { disliked } = this.userPreferences

    this.element.querySelectorAll("[data-ingredient-id]").forEach((ingredient) => {
      const ingredientId = parseInt(ingredient.dataset.ingredientId, 10)
      if (disliked.has(ingredientId)) {
        ingredient.classList.add("bg-red-100", "text-red-950", "font-medium", "rounded-2xl", "my-2", "px-2", 'py-0.5')
      }
    })
  }

  unhighlightAvoids() {
    this.element.querySelectorAll("[data-ingredient-id]").forEach((ingredient) => {
      // Only remove color classes
      ingredient.classList.remove("bg-red-100", "text-red-950")
      // Check if ingredient should still be highlighted by other categories
      this.cleanupBaseClasses(ingredient)
    })
  }

  unhighlightSharedIngredients() {
    // First try to find the target
    if (this.hasSharedIngredientsTarget) {
      const sharedIngredients = JSON.parse(
        this.sharedIngredientsTarget.getAttribute("data-shared-ingredients")
      )
      sharedIngredients.forEach(({ id }) => {
        this.element
          .querySelectorAll(`[data-shared-highlightable="${id}"]`)
          .forEach((element) => {
            // Only remove color classes
            element.classList.remove("bg-indigo-100", "text-indigo-800")
            // Check if ingredient should still be highlighted by other categories
            this.cleanupBaseClasses(element)
          })
      })
    } else {
      // Fallback: look for the data attribute directly
      const sharedIngredientsElement = this.element.querySelector("[data-shared-ingredients]")
      if (sharedIngredientsElement) {
        const sharedIngredients = JSON.parse(
          sharedIngredientsElement.getAttribute("data-shared-ingredients")
        )
        sharedIngredients.forEach(({ id }) => {
          this.element
            .querySelectorAll(`[data-shared-highlightable="${id}"]`)
            .forEach((element) => {
              // Only remove color classes
              element.classList.remove("bg-indigo-100", "text-indigo-800")
              // Check if ingredient should still be highlighted by other categories
              this.cleanupBaseClasses(element)
            })
        })
      }
    }
  }

  cleanupBaseClasses(element) {
    // Check if element still has any highlight color classes
    const hasAnyHighlight =
      element.classList.contains("bg-green-100") ||
      element.classList.contains("bg-red-100") ||
      element.classList.contains("bg-orange-100") ||
      element.classList.contains("bg-indigo-100")

    // If no highlight colors remain, remove base styling classes
    if (!hasAnyHighlight) {
      element.classList.remove("font-medium", "rounded-2xl", "my-2", "px-2", "py-0.5")
    }
  }

  async fetchUserIngredientPreferences() {
    const userElement = document.querySelector("[data-user-id]")
    const userId = userElement ? userElement.dataset.userId : null

    if (!userId) {
      return { liked: new Set(), disliked: new Set(), watched: new Set() }
    }

    const likedKey = `likedIngredientIds_${userId}`
    const dislikedKey = `dislikedIngredientIds_${userId}`
    const watchedKey = `watchedIngredientIds_${userId}`
    const updatedAtKey = `ingredientsUpdatedAt_${userId}`

    const likedIngredientIds = JSON.parse(localStorage.getItem(likedKey)) || []
    const dislikedIngredientIds = JSON.parse(localStorage.getItem(dislikedKey)) || []
    const watchedIngredientIds = JSON.parse(localStorage.getItem(watchedKey)) || []
    const lastUpdated = localStorage.getItem(updatedAtKey)
    const userIngredientsElement = document.querySelector(
      "[data-user-ingredients-updated-at]"
    )
    if (!userIngredientsElement) {
      return { liked: new Set(), disliked: new Set(), watched: new Set() }
    }

    const serverTimestamp = userIngredientsElement
      ? new Date(
        userIngredientsElement
          .getAttribute("data-user-ingredients-updated-at")
          .trim()
      )
      : null

    if (
      !lastUpdated ||
      localStorage.getItem(watchedKey) === null ||
      (serverTimestamp && new Date(lastUpdated) < serverTimestamp)
    ) {
      const response = await fetch("/api/user/ingredients")
      const data = await response.json()
      localStorage.setItem(likedKey, JSON.stringify(data.liked))
      localStorage.setItem(dislikedKey, JSON.stringify(data.disliked))
      localStorage.setItem(watchedKey, JSON.stringify(data.watched || []))
      localStorage.setItem(updatedAtKey, serverTimestamp.toISOString())
      return { liked: new Set(data.liked), disliked: new Set(data.disliked), watched: new Set(data.watched || []) }
    }

    return {
      liked: new Set(likedIngredientIds),
      disliked: new Set(dislikedIngredientIds),
      watched: new Set(watchedIngredientIds),
    }
  }

  async styleIngredientsOnPage() {
    const { liked, disliked, watched } = await this.fetchUserIngredientPreferences()

    this.element.querySelectorAll("[data-ingredient-id]").forEach((ingredient) => {
      const ingredientId = parseInt(ingredient.dataset.ingredientId, 10)
      const userIngredientStyle = ingredient.dataset.userIngredientStyle

      if (liked.has(ingredientId)) {
        ingredient.classList.add("bg-emerald-100", "text-emerald-950", "font-medium", "rounded-2xl", "my-2", "px-4", 'py-0.5')
      } else if (disliked.has(ingredientId)) {
        ingredient.classList.add("bg-red-100", "text-red-950", "font-medium", "rounded-2xl", "my-2", "px-2", 'py-0.5')
      } else if (watched.has(ingredientId)) {
        ingredient.classList.add("bg-orange-100", "text-orange-950", "font-medium", "rounded-2xl", "my-2", "px-2", 'py-0.5')
      }
    })
  }

  setIngredientStepBadge() {
    if (this.hasContainerTarget) {
      this.containerTarget
        .querySelectorAll("[data-ingredient-order-badge]")
        .forEach((badge, index) => {
          badge.innerText = index + 1
        })
    }
  }

  highlightFungalAcneTriggers() {
    if (this.element.querySelector('[data-highlight-fungal-acne="true"]')) {
      this.applyFungalAcneTriggerLevelHighlights()
    }
  }

  highlightPoreCloggingIngredients() {
    if (this.element.querySelector('[data-highlight-pore-clogging="true"]')) {
      this.element
        .querySelectorAll('[data-pore-clogging-highlightable="true"]')
        .forEach((element) => {
          element.classList.add("!bg-red-100")
        })
    }
  }

  highlightSebDermTriggers() {
    if (this.element.querySelector('[data-highlight-seb-derm="true"]')) {
      this.applyFungalAcneTriggerLevelHighlights()
    }
  }

  applyFungalAcneTriggerLevelHighlights() {
    const levelClasses = {
      high: "!bg-red-100",
      normal: "!bg-orange-100",
      low: "!bg-yellow-100",
    }
    this.element
      .querySelectorAll("[data-fungal-acne-trigger-level]")
      .forEach((element) => {
        const klass = levelClasses[element.dataset.fungalAcneTriggerLevel]
        if (klass) element.classList.add(klass)
      })
  }

  highlightSharedIngredients() {
    // First try to find the target
    if (this.hasSharedIngredientsTarget) {
      const sharedIngredients = JSON.parse(
        this.sharedIngredientsTarget.getAttribute("data-shared-ingredients")
      )
      sharedIngredients.forEach(({ id }) => {
        this.element
          .querySelectorAll(`[data-shared-highlightable="${id}"]`)
          .forEach((element) => {
            element.classList.add("bg-indigo-100", "text-indigo-800", "font-medium", "rounded-2xl", "my-2", "px-2", 'py-0.5')
          })
      })
    } else {
      // Fallback: look for the data attribute directly
      const sharedIngredientsElement = this.element.querySelector("[data-shared-ingredients]")
      if (sharedIngredientsElement) {
        const sharedIngredients = JSON.parse(
          sharedIngredientsElement.getAttribute("data-shared-ingredients")
        )
        sharedIngredients.forEach(({ id }) => {
          this.element
            .querySelectorAll(`[data-shared-highlightable="${id}"]`)
            .forEach((element) => {
              element.classList.add("bg-indigo-100", "text-indigo-800", "font-medium", "rounded-2xl", "my-2", "px-2", 'py-0.5')
            })
        })
      }
    }
  }

  setIngredientActivePercentages() {
    if (this.hasActivePercentagesTarget) {
      const activePercentages = JSON.parse(
        this.activePercentagesTarget.getAttribute(
          "data-ingredient-active-percentages"
        )
      )
      activePercentages.forEach(({ id, active_percentage }) => {
        const ingredientNameElement = this.element.querySelector(
          `[data-ingredient-name-for="${id}"]`
        )
        if (
          ingredientNameElement &&
          !ingredientNameElement.innerText.includes("%")
        ) {
          ingredientNameElement.innerText += ` ${active_percentage}`
        }
      })
    }
  }
}